// Loads the Factions page from src/data/factions/*.md: a YAML block between
// --- lines (name, type, emblem, disposition, seat, tags, bases, assets, turnLog,
// relations, history, ...) and the
// faction's description as Markdown below it. The file name is the faction id.
// Files starting with "_" are ignored.

import yaml from "js-yaml";
import assetTypes from "@/data/faction-assets.json";
import tagRules from "@/data/faction-tags.json";
import goalRules from "@/data/faction-goals.json";

const asList = value => (value == null ? [] : Array.isArray(value) ? value : [value]);

// Faction HP by attribute rating (Stars Without Numbers); max HP = 4 + one per attribute
const HP_BY_RATING = [0, 1, 2, 4, 6, 9, 12, 16, 20];
const hpFor = rating => HP_BY_RATING[Math.max(0, Math.min(8, Number(rating) || 0))];

function swnStats(meta) {
	const [force, cunning, wealth] = [meta.force, meta.cunning, meta.wealth].map(n => Number(n) || 0);
	const maxHp = 4 + hpFor(force) + hpFor(cunning) + hpFor(wealth);
	return {
		force,
		cunning,
		wealth,
		hp: meta.hp == null ? maxHp : Number(meta.hp),
		maxHp,
		cred: Number(meta.cred) || 0,
		xp: Number(meta.xp) || 0,
		goal: meta.goal || "",
		goalRule: goalRules[meta.goal] || "",
		goalTarget: meta.goalTarget || "",
		goalDifficulty: Number(meta.goalDifficulty) || 0,
		goalProgress: Number(meta.goalProgress) || 0,
	};
}

// An asset named in a turn log, with its SWN stats for the hover
const assetInfo = name => ({ name: String(name), ...(assetTypes[name] || {}) });

function parseFaction(raw, file) {
	const text = raw.replace(/\r\n/g, "\n");
	const match = text.match(/^---\n([\s\S]*?)\n---\n?([\s\S]*)$/);
	let meta;
	try {
		meta = (match && yaml.load(match[1])) || {};
	} catch (err) {
		console.error(`Skipping faction file ${file}: ${err.message}`);
		return null;
	}
	if (!meta.name) return null;
	// Stealthed bases and assets stay off the dossier
	const visible = list => asList(list).filter(h => h && h.world && !h.stealthed);
	// A homeworld base always sits on the faction's Dawnline seat
	const isHome = b => /homeworld/i.test(b.note || "");
	const bases = visible(meta.bases).map(b => ({
		type: String(b.type || ""),
		world: isHome(b) && meta.seat ? String(meta.seat) : String(b.world),
		homeworld: isHome(b),
		hp: Number(b.hp) || 0,
		maxHp: Number(b.maxHp) || Number(b.hp) || 0,
		// "Homeworld" shows as a tag, so drop it from the note
		note: String(b.note || "")
			.replace(/homeworld\s*(base)?\s*[/,]?\s*/i, "")
			.trim(),
	}));
	// Each asset carries its Stars Without Numbers stats from faction-assets.json
	const assets = visible(meta.assets).map(a => {
		const stats = assetTypes[a.name] || {};
		return {
			...stats,
			name: String(a.name || ""),
			world: String(a.world),
			hp: a.hp ?? stats.hp ?? null,
			maxHp: stats.hp ?? null,
			note: a.note || "",
		};
	});
	return {
		id: file.split("/").pop().replace(/\.md$/, ""),
		name: String(meta.name),
		type: meta.type || "",
		subtype: meta.subtype || "",
		emblem: meta.emblem || "",
		banner: meta.banner || "",
		status: Number(meta.status) || 0,
		politicalAlignment: meta.politicalAlignment || "",
		motto: meta.motto || "",
		homeworld: meta.homeworld || "",
		seat: meta.seat || "",
		demonym: meta.demonym || "",
		// Stars Without Numbers faction stats; null when the faction isn't in the faction game
		swn: meta.force == null ? null : swnStats(meta),
		tags: asList(meta.tags).map(String),
		bases,
		assets,
		// Every world with a base or asset on record
		holdings: [...new Set([...bases, ...assets].map(h => h.world))],
		turnLog: asList(meta.turnLog)
			.filter(t => t && (t.action || t.result))
			.map(t => ({
				turn: String(t.turn ?? ""),
				action: String(t.action ?? ""),
				asset: t.asset ? assetInfo(t.asset) : null,
				target: t.target ? String(t.target) : "",
				world: String(t.world ?? ""),
				defender: t.defender ? assetInfo(t.defender) : null,
				outcome: String(t.outcome ?? ""),
				result: String(t.result ?? ""),
			})),
		// How this faction feels about others: { factionId: -4..+4 }
		relations: meta.relations && typeof meta.relations === "object" ? meta.relations : {},
		// The party's standing over time, oldest first: [{ when, status }]
		history: asList(meta.history)
			.filter(h => h && h.status !== undefined)
			.map(h => ({ when: String(h.when ?? ""), status: Number(h.status) || 0 })),
		notes: match ? match[2].trim() : "",
	};
}

const files = import.meta.glob("@/data/factions/*.md", {
	query: "?raw",
	import: "default",
	eager: true,
});

// Alphabetical, ignoring a leading "The"
const sortName = name => name.replace(/^the\s+/i, "");

export const factions = Object.entries(files)
	.filter(([file]) => !file.split("/").pop().startsWith("_"))
	.map(([file, raw]) => parseFaction(raw, file))
	.filter(Boolean)
	.sort((a, b) => sortName(a.name).localeCompare(sortName(b.name)));

// Rules text for a faction tag; "Planetary Government (DS1)" reads as "Planetary Government"
export const tagRule = tag => tagRules[String(tag).replace(/\s*\(.*\)\s*$/, "")] || "";
